import React, { memo } from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import MarkdownToJsx from 'markdown-to-jsx';
import { List, ListItem, ListLink } from '../List/index';
import { StyledMarkdown } from './StyledMarkdown';

/**
 * Normalize malformed markdown and convert reference-style links to inline.
 * Handles known data inconsistencies in the API data.
 * This issue stemmed from upgrading markdown-to-jsx package
 */
const normalizeMarkdown = (markdown) => {
  if (typeof markdown !== 'string') return markdown;

  // Fix malformed reference-style links/images with space: `] [1]` -> `][1]`
  let normalized = markdown.replace(/\]\s+\[/g, '][');

  // Extract all reference definitions [id]: url
  const references = {};
  const referencePattern = /^\s*\[([^\]]+)\]\s*:\s*(.+)$/gm;
  let match;

  while ((match = referencePattern.exec(normalized)) !== null) {
    references[match[1]] = match[2].trim();
  }

  // If no references found, return normalized markdown
  if (Object.keys(references).length === 0) return normalized;

  // Replace reference definitions with empty string
  normalized = normalized.replace(referencePattern, '');

  // Replace image references ![alt][ref] with ![alt](url)
  normalized = normalized.replace(/!\[([^\]]*)\]\[([^\]]+)\]/g, (fullMatch, alt, ref) => {
    const url = references[ref];
    return url ? `![${alt}](${url})` : fullMatch;
  });

  // Replace link references [text][ref] with [text](url)
  normalized = normalized.replace(/(?<!!)\[([^\]]+)\]\[([^\]]+)\]/g, (fullMatch, text, ref) => {
    const url = references[ref];
    return url ? `[${text}](${url})` : fullMatch;
  });

  return normalized;
};

const options = (onListLinkAnalyticsTrack) => ({
  overrides: {
    ul: List,
    li: ListItem,
    a: { component: ListLink, props: { onListLinkAnalyticsTrack } },
  },
});

/**
 * Styled markdown wrapper using markdown-to-jsx with preprocessing
 * to handle reference-style links and other edge cases.
 */
const Markdown = memo(({
  source, children, onListLinkAnalyticsTrack, ...rest
}) => {
  const content = source || children;

  if (typeof content !== 'string') {
    return null;
  }

  const processedContent = normalizeMarkdown(content);

  return (
    <StyledMarkdown {...rest} options={options(onListLinkAnalyticsTrack)}>
      {processedContent}
    </StyledMarkdown>
  );
});

Markdown.propTypes = {
  children: PropTypes.node,
  /** (DEPRECATED) for backwards compatibility with old react-markdown implementation */
  source: PropTypes.string,
  onListLinkAnalyticsTrack: PropTypes.func,
};

Markdown.defaultProps = {
  children: '',
  source: '',
  onListLinkAnalyticsTrack: noop,
};

export default Markdown;
